import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectLogicMapperSettings, setLogicMapperSettingValue } from '@src/features/interface/interfaceSlice';

type PanelId = 'portInspector' | 'globalVariables' | 'status';

type Position = {
  x: number;
  y: number;
};

type Size = {
  width: number;
  height: number;
};

type UsePanelMinimizeParams = {
  /**
   * Panel identifier
   */
  panelId: PanelId;
  /**
   * Current panel position
   */
  currentPosition: Position;
  /**
   * Current panel size
   */
  currentSize: Size;
  /**
   * Callback to update panel position (called on expand to restore from Redux)
   */
  setPanelPosition: (position: Position) => void;
  /**
   * Callback to update panel size (called on expand to restore from Redux)
   */
  setPanelSize: (size: Size) => void;
};

type UsePanelMinimizeReturn = {
  /**
   * Whether panel is minimized
   */
  isMinimized: boolean;
  /**
   * Minimize handler
   */
  handleMinimize: () => void;
  /**
   * Expand handler
   */
  handleExpand: () => void;
};

/**
 * Shared hook for panel minimize/expand functionality
 * Uses Redux as single source of truth for position and size
 * @param params - Hook parameters
 * @returns Minimize state and handlers
 */
const usePanelMinimize = (params: UsePanelMinimizeParams): UsePanelMinimizeReturn => {
  const { panelId, setPanelPosition, setPanelSize } = params;
  const dispatch = useDispatch();
  const userSettings = useSelector(selectLogicMapperSettings);

  // Get minimized state based on panel ID
  const isMinimized = panelId === 'portInspector'
    ? userSettings.portInspectorMinimized
    : panelId === 'globalVariables'
    ? userSettings.globalVariablesPanelMinimized
    : userSettings.statusPanelMinimized;

  // Get minimized state key based on panel ID
  const minimizedKey = panelId === 'portInspector'
    ? 'portInspectorMinimized'
    : panelId === 'globalVariables'
    ? 'globalVariablesPanelMinimized'
    : 'statusPanelMinimized';

  /**
   * Handles minimizing the panel
   */
  const handleMinimize = useCallback(() => {
    // Position and size are already in Redux (updated on drag/resize)
    // Just update minimized state

    // Update Redux: set minimized to true
    dispatch(setLogicMapperSettingValue({
      key: minimizedKey,
      value: true,
    }));
  }, [panelId, minimizedKey, dispatch]);

  /**
   * Handles expanding the panel
   */
  const handleExpand = useCallback(() => {
    // Restore position and size from Redux (already persisted there)
    const savedPosition = panelId === 'portInspector'
      ? userSettings.portInspectorPosition
      : panelId === 'globalVariables'
      ? userSettings.globalVariablesPanelPosition
      : userSettings.statusPanelPosition;

    const savedSize = panelId === 'portInspector'
      ? userSettings.portInspectorSize
      : panelId === 'globalVariables'
      ? userSettings.globalVariablesPanelSize
      : userSettings.statusPanelSize;

    // Restore position and size
    setPanelPosition(savedPosition);
    setPanelSize(savedSize);

    // Update Redux: set minimized to false
    dispatch(setLogicMapperSettingValue({
      key: minimizedKey,
      value: false,
    }));
  }, [panelId, minimizedKey, userSettings, setPanelPosition, setPanelSize, dispatch]);

  return {
    isMinimized,
    handleMinimize,
    handleExpand,
  };
};

export default usePanelMinimize;

